import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from 'react';
import { Row, Col, FormGroup, Label, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import { authContext } from 'contexts/AuthContext';
import {
  fetchTraders,
  fetchGrades,
  fetchActiveDemands,
  fetchActiveOffers,
  createAdminOrder,
  generateOrderPi,
  generateOrderPo,
  saveGeneratedOrderDocumentUrl,
  getCachedTraders,
  uploadGeneratedOrderDocument,
} from './orderService';
import './AddOrderForm.css';

const AddOrderForm = ({ onClose }) => {
  const { auth } = useContext(authContext);
  const [formData, setFormData] = useState({
    buyer: '',
    seller: '',
    billingAddress: '',
    deliveryAddress: '',
    gradeName: '',
    rate: '',
    orderedQuantity: '',
    bookingDate: '',
    against: 'Demand',
  });

  const [traders, setTraders] = useState([]);
  const [filteredBuyers, setFilteredBuyers] = useState([]);
  const [filteredSellers, setFilteredSellers] = useState([]);
  const [filteredGrades, setFilteredGrades] = useState([]);
  const [combinedAgainstOptions, setCombinedAgainstOptions] = useState([]);
  const [filteredAgainstOptions, setFilteredAgainstOptions] = useState([]);
  const [allDemands, setAllDemands] = useState([]);
  const [allOffers, setAllOffers] = useState([]);
  const [buyerSearch, setBuyerSearch] = useState('');
  const [sellerSearch, setSellerSearch] = useState('');
  const [gradeSearch, setGradeSearch] = useState('');
  const [againstSearch, setAgainstSearch] = useState('');
  const [showBuyerDropdown, setShowBuyerDropdown] = useState(false);
  const [showSellerDropdown, setShowSellerDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [showAgainstDropdown, setShowAgainstDropdown] = useState(false);
  const [selectedDemandId, setSelectedDemandId] = useState(null);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTraders, setLoadingTraders] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [gradeNotAvailable, setGradeNotAvailable] = useState(false);
  const [error, setError] = useState(null);
  const [vehicleArrangeBy, setVehicleArrangeBy] = useState('polystox');
  const [formErrors, setFormErrors] = useState({});

  // Refs for dropdown click outside detection
  const buyerDropdownRef = useRef(null);
  const sellerDropdownRef = useRef(null);
  const gradeDropdownRef = useRef(null);
  const againstDropdownRef = useRef(null);

  const gradeSearchTimeoutRef = useRef(null);
  const buyerSearchTimeoutRef = useRef(null);
  const sellerSearchTimeoutRef = useRef(null);
  const buyerSearchIdRef = useRef(0);
  const sellerSearchIdRef = useRef(0);

  const getTraderCompanyName = (trader) => {
    return (
      trader?.entityName ||
      trader?.tradeName ||
      `${trader?.firstName || ''} ${trader?.lastName || ''}`.trim() ||
      'N/A'
    );
  };

  const getTraderContactName = (trader) => {
    const name = `${trader?.firstName || ''} ${trader?.lastName || ''}`.trim();
    return name || 'N/A';
  };

  const getTraderPhone = (trader) => {
    const code = trader?.callingCode?.callingCode || '';
    const phone = trader?.phone || '';
    if (!code && !phone) return '';
    if (code && phone) return `${code}-${phone}`;
    return code || phone;
  };

  // ✅ Updated to show offer/demand counts
  const getGradeDisplayText = (grade) => {
    const parts = [grade?.name];

    // Add MFI if available
    if (grade?.mfi) {
      parts.push(`mfi ${grade.mfi}`);
    }

    // Add product
    if (grade?.product) {
      parts.push(grade.product);
    }

    // Add manufacturer/subcategory
    if (grade?.productSubCategoryManufacturer) {
      parts.push(grade.productSubCategoryManufacturer);
    }

    const baseText = parts.filter(Boolean).join(' - ');

    // Add offer/demand counts
    const counts = [];
    if (grade?.offerCount > 0) {
      counts.push(
        `${grade.offerCount} offer${grade.offerCount > 1 ? 's' : ''}`,
      );
    }
    if (grade?.demandCount > 0) {
      counts.push(
        `${grade.demandCount} demand${grade.demandCount > 1 ? 's' : ''}`,
      );
    }

    if (counts.length > 0) {
      return (
        <span>
          {baseText}
          <span
            style={{
              fontSize: '11px',
              color: '#666',
              marginLeft: '8px',
              fontWeight: 'normal',
            }}
          >
            ({counts.join(' • ')})
          </span>
        </span>
      );
    }

    return baseText;
  };

  // For string comparison/search
  const getGradeDisplayString = (grade) => {
    const parts = [grade?.name];
    if (grade?.mfi) parts.push(`mfi ${grade.mfi}`);
    if (grade?.product) parts.push(grade.product);
    if (grade?.productSubCategoryManufacturer)
      parts.push(grade.productSubCategoryManufacturer);
    return parts.filter(Boolean).join(' - ');
  };

  // Build combined options
  const buildCombinedOptions = useCallback(
    (demands, offers, filterGradeId = null) => {
      const combined = [];
      const filterGradeIdStr = filterGradeId ? String(filterGradeId) : null;
      const formatUnit = (item) =>
        item?.actualQuantityUnit || item?.quantityUnit || 'MT';
      const formatRate = (rate, unit) => {
        if (rate === null || rate === undefined || rate === '') return '';
        const numericRate = Number(rate);
        if (Number.isNaN(numericRate)) return '';
        return `\u20B9${numericRate.toFixed(2)}/${unit}`;
      };
      const formatQty = (qty, unit) => {
        if (qty === null || qty === undefined || qty === '') return '';
        const numericQty = Number(qty);
        if (Number.isNaN(numericQty)) return '';
        return `${numericQty.toLocaleString()}/${unit}`;
      };
      const formatAgainstDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB');
      };

      const filteredDemands = filterGradeIdStr
        ? demands.filter((demand) =>
          demand.grades.some((g) => String(g.id) === filterGradeIdStr),
        )
        : demands;

      const filteredOffers = filterGradeIdStr
        ? offers.filter((offer) =>
          offer.grades.some((g) => String(g.id) === filterGradeIdStr),
        )
        : offers;

      if (filteredDemands.length > 0) {
        combined.push({ type: 'heading', label: 'DEMANDS' });
        filteredDemands.forEach((demand) => {
          demand.grades.forEach((grade) => {
            if (!filterGradeIdStr || String(grade.id) === filterGradeIdStr) {
              const locationStr =
                demand.city?.cityName && demand.state?.stateName
                  ? `${demand.city.cityName}, ${demand.state.stateName}`
                  : '';

              const quantity = demand.totalQty || 0;
              const unit = formatUnit(demand);
              const primaryLabel = [grade.name, locationStr]
                .filter(Boolean)
                .join(' - ');
              const subLabel = [
                formatRate(demand.rate, unit),
                formatQty(quantity, unit),
                formatAgainstDate(demand.createdAt),
              ]
                .filter(Boolean)
                .join(' | ');

              combined.push({
                type: 'demand',
                demandId: demand.id,
                gradeId: grade.id,
                gradeName: grade.name,
                rate: demand.rate,
                quantity,
                location: locationStr,
                label: primaryLabel,
                subLabel,
                shortLabel: primaryLabel,
              });
            }
          });
        });
      }

      if (filteredOffers.length > 0) {
        combined.push({ type: 'heading', label: 'OFFERS' });
        filteredOffers.forEach((offer) => {
          offer.grades.forEach((grade) => {
            if (!filterGradeIdStr || String(grade.id) === filterGradeIdStr) {
              const locationStr =
                offer.city?.cityName && offer.state?.stateName
                  ? `${offer.city.cityName}, ${offer.state.stateName}`
                  : '';

              const quantity = offer.totalQty || 0;
              const minQty = offer.minimumQty || 0;
              const unit = formatUnit(offer);
              const primaryLabel = [grade.name, locationStr]
                .filter(Boolean)
                .join(' - ');

              const qtyStr = minQty > 0
                ? `Min: ${formatQty(minQty, unit)} | Max: ${formatQty(quantity, unit)}`
                : formatQty(quantity, unit);

              const subLabel = [
                formatRate(offer.rate, unit),
                qtyStr,
                formatAgainstDate(offer.createdAt),
              ]
                .filter(Boolean)
                .join(' | ');

              combined.push({
                type: 'offer',
                offerId: offer.id,
                gradeId: grade.id,
                gradeName: grade.name,
                rate: offer.rate,
                quantity,
                location: locationStr,
                label: primaryLabel,
                subLabel,
                shortLabel: primaryLabel,
              });
            }
          });
        });
      }

      return combined;
    },
    [],
  );

  // Click outside handler for all dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check buyer dropdown
      if (
        buyerDropdownRef.current &&
        !buyerDropdownRef.current.contains(event.target)
      ) {
        setShowBuyerDropdown(false);
      }
      // Check seller dropdown
      if (
        sellerDropdownRef.current &&
        !sellerDropdownRef.current.contains(event.target)
      ) {
        setShowSellerDropdown(false);
      }
      // Check grade dropdown
      if (
        gradeDropdownRef.current &&
        !gradeDropdownRef.current.contains(event.target)
      ) {
        setShowGradeDropdown(false);
      }
      // Check against dropdown
      if (
        againstDropdownRef.current &&
        !againstDropdownRef.current.contains(event.target)
      ) {
        setShowAgainstDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadTraders = useCallback(async () => {
    try {
      const cachedTraders = getCachedTraders();
      if (cachedTraders && cachedTraders.length) {
        setTraders(cachedTraders);
        setFilteredBuyers(cachedTraders);
        setFilteredSellers(cachedTraders);
        return;
      }

      setLoadingTraders(true);
      const tradersData = await fetchTraders();
      setTraders(tradersData);
      setFilteredBuyers(tradersData);
      setFilteredSellers(tradersData);
    } catch (err) {
      setError('Failed to load traders');
      toast.error('Failed to load traders');
    } finally {
      setLoadingTraders(false);
    }
  }, []);

  const loadGrades = useCallback(async (searchString = '') => {
    try {
      setLoadingGrades(true);

      // If user pasted a full display string, extract the first part for the API search
      let apiSearchString = searchString;
      if (searchString && searchString.includes(' - ')) {
        apiSearchString = searchString.split(' - ')[0].trim();
      }

      const gradesData = await fetchGrades(apiSearchString);
      const newFilteredGrades = Array.isArray(gradesData) ? gradesData : [];
      setFilteredGrades(newFilteredGrades);

      // Show "not available" message when search returns no results
      if (searchString && newFilteredGrades.length === 0) {
        setGradeNotAvailable(true);
      } else {
        setGradeNotAvailable(false);
      }

      if (searchString) {
        const normalize = (str) => (str || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();

        // Remove trailing "(X offers)" or "(X demands)" if user copied from dropdown
        const cleanedSearch = searchString.replace(/\(\d+\s+(offer|demand)s?\)\s*$/i, '').trim();
        const normalizedSearch = normalize(cleanedSearch);

        const exactMatch = newFilteredGrades.find(
          (grade) => normalize(getGradeDisplayString(grade)) === normalizedSearch
        );

        if (exactMatch) {
          setFormData((prev) => ({
            ...prev,
            gradeName: exactMatch.id,
          }));
          setGradeSearch(getGradeDisplayString(exactMatch));
          setSelectedGradeId(exactMatch.id);
          setShowGradeDropdown(false);
          setAgainstSearch('');
          setSelectedDemandId(null);
          setSelectedOfferId(null);
        }
      }
    } catch (err) {
      setError('Failed to load grades');
      toast.error('Failed to load grades');
    } finally {
      setLoadingGrades(false);
    }
  }, []);

  const loadDemandsAndOffers = useCallback(async () => {
    try {
      const [demands, offers] = await Promise.all([
        fetchActiveDemands(true),
        fetchActiveOffers(true),
      ]);

      setAllDemands(demands);
      setAllOffers(offers);

      const combined = buildCombinedOptions(demands, offers);
      setCombinedAgainstOptions(combined);
      setFilteredAgainstOptions(combined);
    } catch (err) {
      setError('Failed to load demands and offers');
      toast.error('Failed to load demands and offers');
    }
  }, [buildCombinedOptions]);

  useEffect(() => {
    if (selectedGradeId) {
      const filtered = buildCombinedOptions(
        allDemands,
        allOffers,
        selectedGradeId,
      );
      setFilteredAgainstOptions(filtered);

      if (
        filtered.length === 0 ||
        (filtered.length === 1 && filtered[0].type === 'heading')
      ) {
        setAgainstSearch('');
      }
    } else {
      setFilteredAgainstOptions(combinedAgainstOptions);
    }
  }, [
    selectedGradeId,
    allDemands,
    allOffers,
    combinedAgainstOptions,
    buildCombinedOptions,
  ]);

  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

    setFormData((prevData) => ({
      ...prevData,
      bookingDate: formattedDate,
    }));
  }, []);

  useEffect(() => {
    loadTraders();
    loadGrades('');
    loadDemandsAndOffers();
  }, [loadTraders, loadGrades, loadDemandsAndOffers]);

  useEffect(() => {
    return () => {
      if (gradeSearchTimeoutRef.current) {
        clearTimeout(gradeSearchTimeoutRef.current);
      }
      if (buyerSearchTimeoutRef.current) {
        clearTimeout(buyerSearchTimeoutRef.current);
      }
      if (sellerSearchTimeoutRef.current) {
        clearTimeout(sellerSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleBuyerSearch = (e) => {
    const { value } = e.target;
    setBuyerSearch(value);
    setShowBuyerDropdown(true);

    if (buyerSearchTimeoutRef.current) {
      clearTimeout(buyerSearchTimeoutRef.current);
    }

    const nextValue = value;
    if (!nextValue) {
      setFilteredBuyers(traders);
    }

    buyerSearchTimeoutRef.current = setTimeout(async () => {
      buyerSearchIdRef.current += 1;
      const requestId = buyerSearchIdRef.current;
      try {
        const results = await fetchTraders(nextValue);
        if (buyerSearchIdRef.current !== requestId) return;
        setFilteredBuyers(Array.isArray(results) ? results : []);
        if (!nextValue) {
          setTraders(Array.isArray(results) ? results : []);
        }
      } catch (fetchError) {
        if (buyerSearchIdRef.current !== requestId) return;
        // eslint-disable-next-line no-console
        console.error('Failed to fetch buyers', fetchError);
        setFilteredBuyers([]);
      }
    }, 300);
  };

  const handleSellerSearch = (e) => {
    const { value } = e.target;
    setSellerSearch(value);
    setShowSellerDropdown(true);

    if (sellerSearchTimeoutRef.current) {
      clearTimeout(sellerSearchTimeoutRef.current);
    }

    const nextValue = value;
    if (!nextValue) {
      setFilteredSellers(traders);
    }

    sellerSearchTimeoutRef.current = setTimeout(async () => {
      sellerSearchIdRef.current += 1;
      const requestId = sellerSearchIdRef.current;
      try {
        const results = await fetchTraders(nextValue);
        if (sellerSearchIdRef.current !== requestId) return;
        setFilteredSellers(Array.isArray(results) ? results : []);
        if (!nextValue) {
          setTraders(Array.isArray(results) ? results : []);
        }
      } catch (fetchError) {
        if (sellerSearchIdRef.current !== requestId) return;
        // eslint-disable-next-line no-console
        console.error('Failed to fetch sellers', fetchError);
        setFilteredSellers([]);
      }
    }, 300);
  };

  const handleGradeSearch = (e) => {
    const { value } = e.target;
    setGradeSearch(value);
    setShowGradeDropdown(true);
    if (!value) {
      setGradeNotAvailable(false);
    }
    if (selectedGradeId) {
      setSelectedGradeId(null);
      setSelectedDemandId(null);
      setSelectedOfferId(null);
      setAgainstSearch('');
      setFormData((prev) => ({
        ...prev,
        gradeName: '',
      }));
    }

    if (gradeSearchTimeoutRef.current) {
      clearTimeout(gradeSearchTimeoutRef.current);
    }

    gradeSearchTimeoutRef.current = setTimeout(() => {
      loadGrades(value);
    }, 300);
  };

  const handleAgainstSearch = (e) => {
    const { value } = e.target;
    setAgainstSearch(value);
    setShowAgainstDropdown(true);
  };

  const formatTraderAddress = (addresses = []) => {
    if (!Array.isArray(addresses) || addresses.length === 0) return '';
    const addr = addresses[0] || {};
    const parts = [
      addr.houseNumber,
      addr.street,
      addr.area,
      addr.city?.cityName,
      addr.state?.stateName,
      addr.country?.country,
      addr.pinCode,
    ].filter((part) => part && String(part).trim() !== '');
    return parts.join(', ');
  };

  const selectBuyer = (trader) => {
    const formattedAddress = formatTraderAddress(trader?.addresses);
    setFormData((prev) => ({
      ...prev,
      buyer: trader.id,
      deliveryAddress: formattedAddress || prev.deliveryAddress,
    }));
    const companyName = getTraderCompanyName(trader);
    setBuyerSearch(companyName);
    setShowBuyerDropdown(false);
  };

  const selectSeller = (trader) => {
    const formattedAddress = formatTraderAddress(trader?.addresses);
    setFormData((prev) => ({
      ...prev,
      seller: trader.id,
      billingAddress: formattedAddress || prev.billingAddress,
    }));
    const companyName = getTraderCompanyName(trader);
    setSellerSearch(companyName);
    setShowSellerDropdown(false);
  };

  const selectGrade = (grade) => {
    setFormData({
      ...formData,
      gradeName: grade.id,
    });
    setGradeSearch(getGradeDisplayString(grade));
    setSelectedGradeId(grade.id);
    setShowGradeDropdown(false);

    setAgainstSearch('');
    setSelectedDemandId(null);
    setSelectedOfferId(null);
  };

  const selectAgainstOption = (option) => {
    if (option.type === 'heading') return;
    const matchedGrade = filteredGrades.find(
      (grade) => String(grade.id) === String(option.gradeId),
    );

    setFormData({
      ...formData,
      gradeName: option.gradeId,
      against: option.type === 'demand' ? 'Demand' : 'Offers',
      rate: option.rate.toString(),
      orderedQuantity: option.quantity.toString(),
    });

    setGradeSearch(
      matchedGrade ? getGradeDisplayString(matchedGrade) : option.gradeName,
    );
    setAgainstSearch(option.shortLabel || option.label);
    setSelectedGradeId(option.gradeId);

    if (option.type === 'demand') {
      setSelectedDemandId(option.demandId);
      setSelectedOfferId(null);
    } else {
      setSelectedOfferId(option.offerId);
      setSelectedDemandId(null);
    }

    setShowAgainstDropdown(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let errorMsg = '';

    if (name === 'orderedQuantity' && value !== '') {
      const valNum = parseFloat(value);
      let minAllowed = 1;
      let maxAllowed = Infinity;

      if (formData.against === 'Demand' && selectedDemandId) {
        const selectedDemand = allDemands.find(
          (d) => String(d.id) === String(selectedDemandId)
        );
        if (selectedDemand) {
          maxAllowed = selectedDemand.totalQty;
        }
      } else if (formData.against === 'Offers' && selectedOfferId) {
        const selectedOffer = allOffers.find(
          (o) => String(o.id) === String(selectedOfferId)
        );
        if (selectedOffer) {
          minAllowed = selectedOffer.minimumQty || 1;
          maxAllowed = selectedOffer.totalQty;
        }
      }

      if (valNum < minAllowed) {
        errorMsg = `Quantity must be at least ${minAllowed}`;
      } else if (valNum > maxAllowed) {
        errorMsg = `Quantity cannot exceed ${maxAllowed}`;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });
    setFormErrors({
      ...formErrors,
      [name]: errorMsg,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;
    const newErrors = {};

    if (parseFloat(formData.rate) === 0) {
      newErrors.rate = 'Rate cannot be 0';
      hasError = true;
    }

    const orderedQtyNum = parseFloat(formData.orderedQuantity);
    let minAllowed = 1;
    let maxAllowed = Infinity;

    if (formData.against === 'Demand' && selectedDemandId) {
      const selectedDemand = allDemands.find(
        (d) => String(d.id) === String(selectedDemandId)
      );
      if (selectedDemand) {
        maxAllowed = selectedDemand.totalQty;
      }
    } else if (formData.against === 'Offers' && selectedOfferId) {
      const selectedOffer = allOffers.find(
        (o) => String(o.id) === String(selectedOfferId)
      );
      if (selectedOffer) {
        minAllowed = selectedOffer.minimumQty || 1;
        maxAllowed = selectedOffer.totalQty;
      }
    }

    if (Number.isNaN(orderedQtyNum) || orderedQtyNum < minAllowed) {
      newErrors.orderedQuantity = `Quantity must be at least ${minAllowed}`;
      hasError = true;
    } else if (orderedQtyNum > maxAllowed) {
      newErrors.orderedQuantity = `Quantity cannot exceed ${maxAllowed}`;
      hasError = true;
    }

    if (hasError) {
      setFormErrors(newErrors);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const parseSimpleAddress = (addressString) => {
        const parts = addressString.split(',').map((s) => s.trim());
        return {
          houseNumber: parts[0] || '',
          street: parts.slice(1, -3).join(', ') || '',
          area: parts[parts.length - 3] || '',
          pinCode: parts[parts.length - 1] || '',
          country: 101,
          user: null,
        };
      };

      const deliveryAddr = parseSimpleAddress(formData.deliveryAddress);
      deliveryAddr.user = parseInt(formData.buyer, 10);

      const billingAddr = parseSimpleAddress(formData.billingAddress);
      billingAddr.user = parseInt(formData.seller, 10);

      const orderPayload = {
        orderType: formData.against === 'Demand' ? 'DEMAND' : 'OFFER',
        buyer: parseInt(formData.buyer, 10),
        seller: parseInt(formData.seller, 10),
        grade: parseInt(formData.gradeName, 10),
        quantity: parseFloat(formData.orderedQuantity),
        quantityUnit: 'MT',
        rate: parseFloat(formData.rate),
        rateUnit: 101,
        isLogisticSelf: vehicleArrangeBy !== 'polystox',
        deliveryAddress: deliveryAddr,
        billingAddress: billingAddr,
      };

      if (formData.against === 'Demand' && selectedDemandId) {
        orderPayload.demand = parseInt(selectedDemandId, 10);
      } else if (formData.against === 'Offers' && selectedOfferId) {
        orderPayload.offer = parseInt(selectedOfferId, 10);
      } else if (formData.against === 'Demand') {
        orderPayload.demand = 2319;
      } else {
        orderPayload.offer = 2342;
      }

      const response = await createAdminOrder(orderPayload);
      let documentGenerationFailed = false;

      try {
        const generatedPi = await generateOrderPi(response?.id, {
          orderSequenceNumber: response?.orderSequenceNumber,
        });
        const uploadedPiUrl = await uploadGeneratedOrderDocument(
          generatedPi?.fileSource,
          generatedPi?.fileName,
          auth,
        );
        await saveGeneratedOrderDocumentUrl(
          response?.id,
          'PI',
          uploadedPiUrl,
          auth,
        );
      } catch (documentError) {
        documentGenerationFailed = true;
        // eslint-disable-next-line no-console
        console.error('Automatic PI generation/upload failed', documentError);
      }

      try {
        const generatedPo = await generateOrderPo(response?.id, {
          orderSequenceNumber: response?.orderSequenceNumber,
        });
        const uploadedPoUrl = await uploadGeneratedOrderDocument(
          generatedPo?.fileSource,
          generatedPo?.fileName,
          auth,
        );
        await saveGeneratedOrderDocumentUrl(
          response?.id,
          'PO',
          uploadedPoUrl,
          auth,
        );
      } catch (documentError) {
        documentGenerationFailed = true;
        // eslint-disable-next-line no-console
        console.error('Automatic PO generation/upload failed', documentError);
      }

      toast.dismiss();

      if (documentGenerationFailed) {
        toast.success(
          'Manual order created successfully and PI/PO generated and uploaded as well.',
          {
            position: 'top-right',
            autoClose: 5000,
          },
        );
      } else {
        toast.success(
          'Manual order created successfully and PI/PO generated and uploaded as well.',
          {
            position: 'top-right',
            autoClose: 3000,
          },
        );
      }

      window.dispatchEvent(new Event('orderCreated'));

      setTimeout(() => {
        onClose();
      }, 1000);

      return response;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create order';
      setError(errorMessage);
      toast.error(`${errorMessage}. Please try again.`, {
        position: 'top-right',
        autoClose: 5000,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleBuyerMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
  };

  const handleBuyerMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = 'white';
  };

  const handleSellerMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
  };

  const handleSellerMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = 'white';
  };

  const handleGradeMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
  };

  const handleGradeMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = 'white';
  };

  const handleAgainstMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
  };

  const handleAgainstMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = 'white';
  };

  const hasAgainstOptions = Boolean(
    selectedGradeId &&
    filteredAgainstOptions.some((option) => option.type !== 'heading'),
  );

  return (
    <div className="add-order-form-wrapper">
      <form onSubmit={handleSubmit}>
        <div className="form-content">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="form-section">
            <div className="form-section-header">
              <h5>Personal Details</h5>
            </div>
            <div className="form-section-body">
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label">
                      Buyer <span className="text-danger">*</span>
                    </Label>
                    <div
                      style={{ position: 'relative' }}
                      ref={buyerDropdownRef}
                    >
                      <Input
                        type="text"
                        className="form-control"
                        value={buyerSearch}
                        onChange={handleBuyerSearch}
                        onFocus={() => setShowBuyerDropdown(true)}
                        placeholder={
                          loadingTraders
                            ? 'Loading buyers...'
                            : 'Buyer name or company'
                        }
                        disabled={loadingTraders}
                        required
                      />
                      {showBuyerDropdown && filteredBuyers.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {filteredBuyers.map((trader) => {
                            const companyName = getTraderCompanyName(trader);
                            const contactName = getTraderContactName(trader);
                            const phone = getTraderPhone(trader);
                            const showMeta = contactName !== 'N/A' || phone;
                            return (
                              <div
                                key={trader.id}
                                onClick={() => selectBuyer(trader)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    selectBuyer(trader);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                }}
                                onMouseEnter={handleBuyerMouseEnter}
                                onMouseLeave={handleBuyerMouseLeave}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {companyName}
                                </div>
                                {showMeta && (
                                  <div
                                    style={{ fontSize: '11px', color: '#666' }}
                                  >
                                    {contactName === 'N/A' ? '' : contactName}
                                    {phone
                                      ? `${contactName === 'N/A' ? '' : ' \u2022 '}${phone}`
                                      : ''}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label">
                      Seller <span className="text-danger">*</span>
                    </Label>
                    <div
                      style={{ position: 'relative' }}
                      ref={sellerDropdownRef}
                    >
                      <Input
                        type="text"
                        className="form-control"
                        value={sellerSearch}
                        onChange={handleSellerSearch}
                        onFocus={() => setShowSellerDropdown(true)}
                        placeholder={
                          loadingTraders
                            ? 'Loading sellers...'
                            : 'Seller name or company'
                        }
                        disabled={loadingTraders}
                        required
                      />
                      {showSellerDropdown && filteredSellers.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {filteredSellers.map((trader) => {
                            const companyName = getTraderCompanyName(trader);
                            const contactName = getTraderContactName(trader);
                            const phone = getTraderPhone(trader);
                            const showMeta = contactName !== 'N/A' || phone;
                            return (
                              <div
                                key={trader.id}
                                onClick={() => selectSeller(trader)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    selectSeller(trader);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                }}
                                onMouseEnter={handleSellerMouseEnter}
                                onMouseLeave={handleSellerMouseLeave}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {companyName}
                                </div>
                                {showMeta && (
                                  <div
                                    style={{ fontSize: '11px', color: '#666' }}
                                  >
                                    {contactName === 'N/A' ? '' : contactName}
                                    {phone
                                      ? `${contactName === 'N/A' ? '' : ' \u2022 '}${phone}`
                                      : ''}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label">Delivery Address</Label>
                    <Input
                      type="textarea"
                      name="deliveryAddress"
                      className="form-control"
                      value={formData.deliveryAddress}
                      onChange={handleInputChange}
                      placeholder="Enter delivery address"
                      rows="2"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label">Billing Address</Label>
                    <Input
                      type="textarea"
                      name="billingAddress"
                      className="form-control"
                      value={formData.billingAddress}
                      onChange={handleInputChange}
                      placeholder="Enter billing address"
                      rows="2"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <h5>Order Details</h5>
            </div>
            <div className="form-section-body">
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label className="form-label">
                      Grade Name <span className="text-danger">*</span>
                    </Label>
                    <div
                      style={{ position: 'relative' }}
                      ref={gradeDropdownRef}
                    >
                      <Input
                        type="text"
                        className="form-control"
                        value={gradeSearch}
                        onChange={handleGradeSearch}
                        onFocus={() => setShowGradeDropdown(true)}
                        placeholder={
                          loadingGrades ? 'Loading grades...' : 'Grade Name'
                        }
                        required
                      />
                      {showGradeDropdown && filteredGrades.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {filteredGrades.map((grade) => {
                            const displayText = getGradeDisplayString(grade);
                            return (
                              <div
                                key={grade.id}
                                onClick={() => selectGrade(grade)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    selectGrade(grade);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                onMouseEnter={handleGradeMouseEnter}
                                onMouseLeave={handleGradeMouseLeave}
                                title={displayText}
                              >
                                {getGradeDisplayText(grade)}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {gradeNotAvailable && !loadingGrades && (
                      <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                        Grade is not available on the platform
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {hasAgainstOptions && (
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label className="form-label">Against</Label>
                      <div
                        style={{ position: 'relative' }}
                        ref={againstDropdownRef}
                      >
                        <Input
                          type="text"
                          className="form-control"
                          value={againstSearch}
                          onChange={handleAgainstSearch}
                          onFocus={() => setShowAgainstDropdown(true)}
                          placeholder="Select Demand or Offer"
                          disabled={!selectedGradeId}
                        />
                        {showAgainstDropdown &&
                          filteredAgainstOptions.length > 0 && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                maxHeight: '250px',
                                overflowY: 'auto',
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                zIndex: 1000,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              }}
                            >
                              {filteredAgainstOptions.map((option, index) => {
                                const uniqueKey =
                                  option.type === 'heading'
                                    ? `heading-${index}`
                                    : `${option.type}-${option.demandId || option.offerId}-${option.gradeId}`;

                                return option.type === 'heading' ? (
                                  <div
                                    key={uniqueKey}
                                    style={{
                                      padding: '8px 12px',
                                      backgroundColor: '#f5f5f5',
                                      fontWeight: 'bold',
                                      fontSize: '12px',
                                      color: '#666',
                                      textTransform: 'uppercase',
                                      borderBottom: '2px solid #ddd',
                                    }}
                                  >
                                    {option.label}
                                  </div>
                                ) : (
                                  <div
                                    key={uniqueKey}
                                    onClick={() => selectAgainstOption(option)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        selectAgainstOption(option);
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    style={{
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #f0f0f0',
                                      paddingLeft: '24px',
                                      fontSize: '13px',
                                    }}
                                    onMouseEnter={handleAgainstMouseEnter}
                                    onMouseLeave={handleAgainstMouseLeave}
                                  >
                                    <div style={{ fontWeight: 600 }}>
                                      {option.label}
                                    </div>
                                    {option.subLabel && (
                                      <div
                                        style={{
                                          fontSize: '11px',
                                          color: '#666',
                                        }}
                                      >
                                        {option.subLabel}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label className="form-label">
                      ₹ Rate <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="rate"
                      className="form-control"
                      value={formData.rate}
                      onChange={handleInputChange}
                      placeholder="Rate"
                      step="0.01"
                      required
                    />
                    {formErrors.rate && (
                      <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                        {formErrors.rate}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label className="form-label">
                      Quantity/MT <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="orderedQuantity"
                      className="form-control"
                      value={formData.orderedQuantity}
                      onChange={handleInputChange}
                      placeholder="Quantity"
                      step="0.01"
                      min={
                        formData.against === 'Offers' && selectedOfferId
                          ? allOffers.find((o) => String(o.id) === String(selectedOfferId))?.minimumQty || 1
                          : 1
                      }
                      // eslint-disable-next-line no-nested-ternary
                      max={
                        formData.against === 'Demand' && selectedDemandId
                          ? allDemands.find((d) => String(d.id) === String(selectedDemandId))?.totalQty
                          : formData.against === 'Offers' && selectedOfferId
                            ? allOffers.find((o) => String(o.id) === String(selectedOfferId))?.totalQty
                            : undefined
                      }
                      required
                    />
                    {formErrors.orderedQuantity && (
                      <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                        {formErrors.orderedQuantity}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label className="form-label">
                      Booking Date <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="datetime-local"
                      name="bookingDate"
                      className="form-control"
                      value={formData.bookingDate}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>

              <div className="vehicle-arrangement-card">
                <div className="vehicle-arrangement-title">
                  Vehicle to be arranged by
                </div>

                <label
                  className="vehicle-radio-option"
                  htmlFor="arrange-polystox"
                >
                  <Input
                    id="arrange-polystox"
                    type="radio"
                    name="vehicleArrangeBy"
                    checked={vehicleArrangeBy === 'polystox'}
                    onChange={() => setVehicleArrangeBy('polystox')}
                  />
                  <span className="vehicle-radio-label">Polystox</span>
                </label>

                {vehicleArrangeBy === 'polystox' && (
                  <div className="vehicle-polystox-note">
                    <strong>Freight charges will be applicable</strong> for
                    vehicle arranged by Polystox. Kindly ensure to check before
                    proceeding. In case of any concern, you can choose to
                    arrange your own vehicle.
                  </div>
                )}

                <label className="vehicle-radio-option" htmlFor="arrange-self">
                  <Input
                    id="arrange-self"
                    type="radio"
                    name="vehicleArrangeBy"
                    checked={vehicleArrangeBy === 'self'}
                    onChange={() => setVehicleArrangeBy('self')}
                  />
                  <span className="vehicle-radio-label">Vendor</span>
                </label>

                {vehicleArrangeBy === 'self' && (
                  <div className="vehicle-self-note">
                    Vendor will arranged their own vehicle
                  </div>
                )}

                <label
                  className="vehicle-radio-option"
                  htmlFor="arrange-ex-warehouse"
                >
                  <Input
                    id="arrange-ex-warehouse"
                    type="radio"
                    name="vehicleArrangeBy"
                    checked={vehicleArrangeBy === 'exWarehouse'}
                    onChange={() => setVehicleArrangeBy('exWarehouse')}
                  />
                  <span className="vehicle-radio-label">Ex - Warehouse</span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-cancel"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-create" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddOrderForm;
